import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import {
  Wc,
  CalendarToday,
  Bloodtype,
  Phone,
  Home,
  Badge,
  Person,
} from "@mui/icons-material";
import MedicalHistory from "./MedicalHistory/MedicalHistory";
import ActiveAdmissions from "./ActiveAdmissions";
import ActivePrescriptions from "./ActivePrescriptions";
import QuickStats from "./QuickStats";
import ComprehensiveProfile from "./ComprehensiveProfile";
import PatientTicketsList from "./PatientTicketsList";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const userProfile = location.state;

  if (!userProfile || !userProfile.results || !userProfile.results[0]) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Typography variant="h6" color="textSecondary">
          {t("profile.noPatientSelected")}
        </Typography>
      </Box>
    );
  }

  const {
    firstname,
    lastname,
    id,
    gender,
    age,
    blood_type_display,
    phone,
    address,
    passport,
    father_name,
    follow_up_details,
  } = userProfile.results[0];

  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", mt: 2, width: "100%" }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            {/* Left Side: Patient Details */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                {/* Full Name */}
                <Grid item xs={12}>
                  <Typography variant="h5" fontWeight="bold">
                    {firstname} {lastname}
                  </Typography>
                </Grid>

                {/* Patient ID */}
                <Grid item xs={12}>
                  <Chip
                    label={`${t("profile.patientId")}: ${id}`}
                    color="primary"
                    size="small"
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Wc fontSize="small" sx={{ mr: 1, color: "#1976d2" }} />
                    <Typography variant="body1">
                      <strong>{t("profile.gender")}:</strong>{" "}
                      {gender === "1" || gender === 1
                        ? t("profile.male")
                        : t("profile.female")}
                    </Typography>
                  </Box>
                </Grid>

                {/* Age */}
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <CalendarToday
                      fontSize="small"
                      sx={{ mr: 1, color: "#1976d2" }}
                    />
                    <Typography variant="body1">
                      <strong>{t("profile.age")}:</strong> {age || "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Blood Type */}
                <Grid item xs={6} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Bloodtype
                      fontSize="small"
                      sx={{ mr: 1, color: "#1976d2" }}
                    />
                    <Typography variant="body1">
                      <strong>{t("profile.bloodType")}:</strong>{" "}
                      {blood_type_display || t("profile.unknown")}
                    </Typography>
                  </Box>
                </Grid>

                {/* Phone */}
                {phone && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Phone
                        fontSize="small"
                        sx={{ mr: 1, color: "#1976d2" }}
                      />
                      <Typography variant="body1">
                        <strong>{t("profile.phone")}:</strong> {phone}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Address */}
                {address && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
                      <Home
                        fontSize="small"
                        sx={{ mr: 1, color: "#1976d2", mt: 0.5 }}
                      />
                      <Typography variant="body1">
                        <strong>{t("profile.address")}:</strong> {address}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Passport */}
                {passport && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Badge
                        fontSize="small"
                        sx={{ mr: 1, color: "#1976d2" }}
                      />
                      <Typography variant="body1">
                        <strong>{t("profile.passport")}:</strong> {passport}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Father's Name */}
                {father_name && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Person
                        fontSize="small"
                        sx={{ mr: 1, color: "#1976d2" }}
                      />
                      <Typography variant="body1">
                        <strong>{t("profile.fatherName")}:</strong>{" "}
                        {father_name}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Follow-up Doctor */}
                {follow_up_details && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Person
                        fontSize="small"
                        sx={{ mr: 1, color: "#1976d2" }}
                      />
                      <Typography variant="body1">
                        <strong>{t("profile.followUpBy")}:</strong>{" "}
                        {follow_up_details.fullname}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Right Side: Avatar and Name */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar sx={{ bgcolor: "#1976d2", width: 150, height: 150 }}>
                {firstname.charAt(0)}
              </Avatar>
              <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                {firstname} {lastname}
              </Typography>
            </Grid>
          </Grid>
          {/* Divider */}
          <Divider sx={{ my: 3 }} />
          
          {/* Quick Stats Section */}
          <QuickStats patientId={id} />
          
          {/* Divider */}
          <Divider sx={{ my: 3 }} />
          
          {/* Active Admissions Section */}
          <ActiveAdmissions patientId={id} />
          
          {/* Divider */}
          <Divider sx={{ my: 3 }} />
          
          {/* Active Prescriptions Section */}
          <ActivePrescriptions patientId={id} />
          
          {/* Divider */}
          <Divider sx={{ my: 3 }} />
          
          {/* All tickets for this patient */}
          <PatientTicketsList patientId={id} userProfile={userProfile} />
          
          {/* Divider */}
          <Divider sx={{ my: 3 }} />
          
          {/* Comprehensive Profile Section */}
          <ComprehensiveProfile
            patientId={id}
            ticketId={null}
            showFullHistory={true}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;
